import { c as _c } from "react/compiler-runtime";
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { ChoiceButtonStyle, ErrorDetail, VaultKind } from '@onefootprint/request-types/dashboard';
import { InlineAlert, type TemplateInputProps, useConfirmationDialog } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BifrostFrame, PreviewPane } from '@/components/playbook-builder/components/shared/node-preview';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import useMobileLayout from '@/hooks/use-mobile-layout';
import AddContentDropdown from './components/add-content-dropdown';
import DraggableQuestion from './components/draggable-question';
import PageSettings, { type PageSection } from './components/page-settings';
import QuestionPreview from './components/question-preview';
import QuestionSettings, { type QuestionSettingsRef } from './components/question-settings';
import QuestionContainer from './components/questions/question-container';
import TemplatedText from './components/templated-text';
import useAutoButtonValues from './hooks/use-auto-button-values';
import useAutoChoiceIdentifier from './hooks/use-auto-choice-identifier';
import { SubjectProvider } from './hooks/use-subject';
import type { CustomQuestionsFormData } from './types';
import { findFirstChoiceError } from './utils/choice-errors';
import { getConditionUpdates } from './utils/condition-cleanup';
type CustomQuestionsFormProps = {
  defaultValues?: Partial<CustomQuestionsFormData>;
  onSubmit: (data: CustomQuestionsFormData) => void;
  autocompleteItems?: TemplateInputProps['autocompleteItems'];
  errors?: ErrorDetail[];
  canEdit?: boolean;
  subject: VaultKind;
};

// Spans, not Buttons: the card sits inside the drawer's form, where a real button would submit it,
// and each one sits inside the button that selects it. A link gets a row as tall as a button, as
// PageShell gives a stacked link at runtime.
const PreviewCta = t0 => {
  const $ = _c(3);
  const {
    variant,
    text
  } = t0;
  let t1;
  if ($[0] !== text || $[1] !== variant) {
    t1 = variant === "link" ? <span className="flex h-10 items-center justify-center"><span className="fp-link-button fp-custom-appearance text-label-2">{text}</span></span> : <span className="fp-button fp-custom-appearance text-label-2" data-variant={variant} data-size="large" data-full-width="true">{text}</span>;
    $[0] = text;
    $[1] = variant;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const PlaybookCustomQuestions = t0 => {
  const $ = _c(8);
  let props;
  let subject;
  if ($[0] !== t0) {
    ({
      subject,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = subject;
  } else {
    props = $[1];
    subject = $[2];
  }
  let t1;
  if ($[3] !== props) {
    t1 = <CustomQuestionsForm {...props} />;
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== subject || $[6] !== t1) {
    t2 = <SubjectProvider value={subject}>{t1}</SubjectProvider>;
    $[5] = subject;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const CustomQuestionsForm = t0 => {
  const $ = _c(204);
  const {
    defaultValues,
    onSubmit,
    autocompleteItems,
    errors,
    canEdit: t1
  } = t0;
  const canEdit = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation("playbook-custom-questions");
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "nodePreview"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t: tPreview
  } = useTranslation("playbook-builder", t2);
  const confirm = useConfirmationDialog();
  const {
    toggle
  } = useDirty();
  const isMobile = useMobileLayout();
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [selectedButtonId, setSelectedButtonId] = useState(null);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      basic: true,
      buttons: true,
      confirmation: true
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [openSections, setOpenSections] = useState(t3);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [openButtonIds, setOpenButtonIds] = useState(t4);
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (section, open) => setOpenSections(current => ({
      ...current,
      [section]: open
    }));
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const setSectionOpen = t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (id, open_0) => setOpenButtonIds(current_0 => ({
      ...current_0,
      [id]: open_0
    }));
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const setButtonOpen = t6;
  const previewAreaRef = useRef(null);
  const pageHeaderRef = useRef(null);
  const settingsAreaRef = useRef(null);
  const questionSettingsRef = useRef(null);
  const t7 = !canEdit;
  const t8 = defaultValues?.title || "";
  const t9 = defaultValues?.subtitle || "";
  let t10;
  if ($[5] !== defaultValues?.questions) {
    t10 = defaultValues?.questions || [];
    $[5] = defaultValues?.questions;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const t11 = defaultValues?.confirmationMode || "confirm";
  let t12;
  if ($[7] !== defaultValues?.choice) {
    t12 = defaultValues?.choice ?? {
      saveValue: false,
      dataIdentifier: "",
      buttons: []
    };
    $[7] = defaultValues?.choice;
    $[8] = t12;
  } else {
    t12 = $[8];
  }
  let t13;
  if ($[9] !== t10 || $[10] !== t11 || $[11] !== t12 || $[12] !== t8 || $[13] !== t9) {
    t13 = {
      title: t8,
      subtitle: t9,
      questions: t10,
      confirmationMode: t11,
      choice: t12
    };
    $[9] = t10;
    $[10] = t11;
    $[11] = t12;
    $[12] = t8;
    $[13] = t9;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== t13 || $[16] !== t7) {
    t14 = {
      mode: "onChange",
      disabled: t7,
      defaultValues: t13
    };
    $[15] = t13;
    $[16] = t7;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const formMethods = useForm(t14);
  const {
    control,
    trigger,
    formState,
    setError
  } = formMethods;
  let t15;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = ["title", "subtitle", "choice.buttons"];
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== control) {
    t16 = {
      control,
      name: t15
    };
    $[19] = control;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  const [title, subtitle, buttons] = useWatch(t16);
  useAutoChoiceIdentifier(formMethods);
  useAutoButtonValues(formMethods);
  let t17;
  let t18;
  if ($[21] !== formState.isDirty || $[22] !== toggle) {
    t17 = () => {
      toggle("custom-questions-form", formState.isDirty);
    };
    t18 = [formState.isDirty, toggle];
    $[21] = formState.isDirty;
    $[22] = toggle;
    $[23] = t17;
    $[24] = t18;
  } else {
    t17 = $[23];
    t18 = $[24];
  }
  useEffect(t17, t18);
  let t19;
  if ($[25] !== control) {
    t19 = {
      control,
      name: "questions",
      keyName: "_fieldId"
    };
    $[25] = control;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  const {
    fields: questions,
    append,
    move,
    replace
  } = useFieldArray(t19);
  let t20;
  if ($[27] !== trigger) {
    t20 = index => {
      if (index !== null && index !== "header") {
        trigger(`questions.${index}`);
      }
    };
    $[27] = trigger;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  const validateQuestion = t20;
  let t21;
  if ($[29] !== append || $[30] !== questions.length || $[31] !== selectedQuestionIndex || $[32] !== t || $[33] !== validateQuestion) {
    t21 = contentType => {
      const generateId = _temp;
      const id_0 = generateId();
      validateQuestion(selectedQuestionIndex);
      setSelectedButtonId(null);
      if (contentType === "text_block") {
        append({
          id: id_0,
          kind: "text_block" as const,
          content: ""
        });
        return setSelectedQuestionIndex(questions.length);
      }
      if (contentType === "short_text") {
        append({
          id: id_0,
          kind: "short_text" as const,
          label: "",
          dataIdentifier: "",
          required: true,
          format: "any" as const
        });
        return setSelectedQuestionIndex(questions.length);
      }
      if (contentType === "long_text") {
        append({
          id: id_0,
          kind: "long_text" as const,
          label: "",
          dataIdentifier: "",
          required: true
        });
        setSelectedQuestionIndex(questions.length);
        return;
      }
      if (contentType === "number") {
        append({
          id: id_0,
          kind: "number" as const,
          label: "",
          dataIdentifier: "",
          required: true,
          numberFormat: "any"
        });
        return setSelectedQuestionIndex(questions.length);
      }
      if (contentType === "dropdown") {
        append({
          id: id_0,
          kind: "dropdown" as const,
          label: "",
          dataIdentifier: "",
          required: true,
          options: [{
            value: "option-1",
            label: t(_temp2)
          }, {
            value: "option-2",
            label: t(_temp3)
          }],
          includeOther: false
        });
        return setSelectedQuestionIndex(questions.length);
      }
      if (contentType === "multi-select") {
        append({
          id: id_0,
          kind: "checkboxes" as const,
          label: "",
          dataIdentifier: "",
          required: true,
          options: [{
            value: "option-1",
            label: t(_temp4)
          }, {
            value: "option-2",
            label: t(_temp5)
          }]
        });
        return setSelectedQuestionIndex(questions.length);
      }
      if (contentType === "checkbox") {
        append({
          id: id_0,
          kind: "checkbox" as const,
          label: "",
          dataIdentifier: "",
          required: true
        });
        return setSelectedQuestionIndex(questions.length);
      }
      if (contentType === "multiple_choice") {
        append({
          id: id_0,
          kind: "multiple_choice" as const,
          label: "",
          dataIdentifier: "",
          required: true,
          options: [{
            value: "option-1",
            label: t(_temp6)
          }, {
            value: "option-2",
            label: t(_temp7)
          }]
        });
        return setSelectedQuestionIndex(questions.length);
      }
      if (contentType === "address") {
        append({
          id: id_0,
          kind: "address" as const,
          label: "",
          dataIdentifier: "",
          required: true,
          allowPoBox: false
        });
        return setSelectedQuestionIndex(questions.length);
      }
    };
    $[29] = append;
    $[30] = questions.length;
    $[31] = selectedQuestionIndex;
    $[32] = t;
    $[33] = validateQuestion;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  const handleAddContent = t21;
  let t22;
  if ($[35] !== t) {
    t22 = t(_temp8);
    $[35] = t;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  const stockLabel = t22;
  let t23;
  if ($[37] !== stockLabel) {
    t23 = choice => {
      const [only, ...rest] = choice.buttons;
      return !choice.saveValue && rest.length === 0 && only?.label.trim() === stockLabel;
    };
    $[37] = stockLabel;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  const isStockContinue = t23;
  let t24;
  if ($[39] !== formMethods || $[40] !== isStockContinue || $[41] !== onSubmit || $[42] !== setError || $[43] !== t) {
    let t25;
    if ($[45] !== isStockContinue || $[46] !== onSubmit || $[47] !== setError || $[48] !== t) {
      t25 = submitted => {
        const data = isStockContinue(submitted.choice) ? {
          ...submitted,
          choice: {
            ...submitted.choice,
            buttons: []
          }
        } : submitted;
        const unfinished = data.questions.findIndex(_temp9);
        if (unfinished !== -1) {
          setError(`questions.${unfinished}.dataIdentifier`, {
            type: "required",
            message: t(_temp10)
          });
          setSelectedButtonId(null);
          setSelectedQuestionIndex(unfinished);
          return;
        }
        const choiceError = findFirstChoiceError(data.choice);
        if (choiceError) {
          setSelectedQuestionIndex(null);
          setSectionOpen("buttons", true);
          const message = t($_7 => $_7.buttons.errors[choiceError.key]);
          if (choiceError.field === "dataIdentifier") {
            setError("choice.dataIdentifier", {
              message
            });
          } else {
            const failing = data.choice.buttons[choiceError.index];
            if (failing) {
              setButtonOpen(failing.id, true);
            }
            setError(`choice.buttons.${choiceError.index}.${choiceError.field}`, {
              message
            });
          }
          return;
        }
        onSubmit?.(data);
      };
      $[45] = isStockContinue;
      $[46] = onSubmit;
      $[47] = setError;
      $[48] = t;
      $[49] = t25;
    } else {
      t25 = $[49];
    }
    t24 = formMethods.handleSubmit(t25);
    $[39] = formMethods;
    $[40] = isStockContinue;
    $[41] = onSubmit;
    $[42] = setError;
    $[43] = t;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  const handleSubmit = t24;
  let t25;
  if ($[50] !== formMethods) {
    t25 = next => formMethods.setValue("choice.buttons", next, {
      shouldDirty: true
    });
    $[50] = formMethods;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  const setButtons = t25;
  const newButton = _temp11;
  let t26;
  if ($[52] !== selectedQuestionIndex || $[53] !== validateQuestion) {
    t26 = id_1 => {
      validateQuestion(selectedQuestionIndex);
      setSelectedQuestionIndex(null);
      setSelectedButtonId(id_1);
      setSectionOpen("buttons", true);
      setButtonOpen(id_1, true);
    };
    $[52] = selectedQuestionIndex;
    $[53] = validateQuestion;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  const selectButton = t26;
  let t27;
  if ($[55] !== selectButton || $[56] !== selectedButtonId) {
    t27 = id_2 => {
      if (selectedButtonId === id_2) {
        setSelectedButtonId(null);
      } else {
        selectButton(id_2);
      }
    };
    $[55] = selectButton;
    $[56] = selectedButtonId;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  const toggleSelectedButton = t27;
  let t28;
  if ($[58] !== formMethods || $[59] !== stockLabel) {
    t28 = () => {
      const stock = newButton(stockLabel, "primary");
      formMethods.setValue("choice.buttons", [stock]);
      return stock.id;
    };
    $[58] = formMethods;
    $[59] = stockLabel;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  const materialiseStockButton = t28;
  let t29;
  if ($[61] !== materialiseStockButton || $[62] !== selectButton) {
    t29 = () => selectButton(materialiseStockButton());
    $[61] = materialiseStockButton;
    $[62] = selectButton;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  const handleSelectStockButton = t29;
  let t30;
  if ($[64] !== materialiseStockButton) {
    t30 = () => setButtonOpen(materialiseStockButton(), true);
    $[64] = materialiseStockButton;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  const handleOpenStockButton = t30;
  let t31;
  if ($[66] !== buttons || $[67] !== setButtons || $[68] !== stockLabel) {
    t31 = () => {
      const added = newButton();
      setButtons(buttons.length === 0 ? [newButton(stockLabel, "primary"), added] : [...buttons, added]);
      setSectionOpen("buttons", true);
      setButtonOpen(added.id, true);
    };
    $[66] = buttons;
    $[67] = setButtons;
    $[68] = stockLabel;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  const handleAddButton = t31;
  let t32;
  if ($[70] !== buttons.length || $[71] !== formMethods || $[72] !== setButtons || $[73] !== stockLabel) {
    t32 = saveValue => {
      if (!saveValue) {
        return;
      }
      if (formMethods.getValues("confirmationMode") === "skip") {
        formMethods.setValue("confirmationMode", "confirm", {
          shouldDirty: true
        });
      }
      if (buttons.length > 0) {
        return;
      }
      const stock_0 = newButton(stockLabel, "primary");
      setButtons([stock_0]);
      setButtonOpen(stock_0.id, true);
    };
    $[70] = buttons.length;
    $[71] = formMethods;
    $[72] = setButtons;
    $[73] = stockLabel;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  const handleSaveValueChange = t32;
  let t33;
  if ($[75] !== buttons || $[76] !== formMethods || $[77] !== selectedButtonId || $[78] !== setButtons) {
    t33 = id_3 => {
      setButtons(buttons.filter(button => button.id !== id_3));
      formMethods.clearErrors("choice");
      if (selectedButtonId === id_3) {
        setSelectedButtonId(null);
      }
    };
    $[75] = buttons;
    $[76] = formMethods;
    $[77] = selectedButtonId;
    $[78] = setButtons;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  const handleDeleteButton = t33;
  let t34;
  if ($[80] !== buttons || $[81] !== confirm || $[82] !== formMethods || $[83] !== handleDeleteButton || $[84] !== t) {
    t34 = id_4 => {
      const value = formMethods.getValues("choice.saveValue") ? buttons.find(button_0 => button_0.id === id_4)?.value.trim() : undefined;
      confirm.open({
        title: t(_temp12),
        description: value ? t(_temp13, {
          value
        }) : t(_temp14),
        variant: "destructive",
        primaryButton: {
          label: t(_temp15),
          onClick: () => handleDeleteButton(id_4)
        },
        secondaryButton: {
          label: t(_temp16)
        }
      });
    };
    $[80] = buttons;
    $[81] = confirm;
    $[82] = formMethods;
    $[83] = handleDeleteButton;
    $[84] = t;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  const confirmDeleteButton = t34;
  let t35;
  if ($[86] !== confirmDeleteButton || $[87] !== formState.dirtyFields || $[88] !== handleDeleteButton) {
    t35 = (id_5, index_0) => {
      if (formState.dirtyFields.choice?.buttons?.[index_0]) {
        confirmDeleteButton(id_5);
      } else {
        handleDeleteButton(id_5);
      }
    };
    $[86] = confirmDeleteButton;
    $[87] = formState.dirtyFields;
    $[88] = handleDeleteButton;
    $[89] = t35;
  } else {
    t35 = $[89];
  }
  const handleDeletePanelButton = t35;
  let t36;
  if ($[90] !== selectedButtonId) {
    t36 = open_1 => {
      if (!open_1) {
        return;
      }
      if (selectedButtonId) {
        setButtonOpen(selectedButtonId, false);
      }
      setSelectedButtonId(null);
      setSelectedQuestionIndex(_temp17);
    };
    $[90] = selectedButtonId;
    $[91] = t36;
  } else {
    t36 = $[91];
  }
  const handleAddContentOpenChange = t36;
  let t37;
  if ($[92] !== buttons || $[93] !== setButtons) {
    t37 = t38 => {
      const {
        active,
        over
      } = t38;
      if (!over || active.id === over.id) {
        return;
      }
      const from = buttons.findIndex(button_1 => button_1.id === active.id);
      const to = buttons.findIndex(button_2 => button_2.id === over.id);
      setButtons(arrayMove(buttons, from, to));
    };
    $[92] = buttons;
    $[93] = setButtons;
    $[94] = t37;
  } else {
    t37 = $[94];
  }
  const handleButtonDragEnd = t37;
  let t38;
  if ($[95] !== formMethods) {
    t38 = excludeIdentifiers => {
      const currentQuestions = formMethods.getValues("questions");
      const updates = getConditionUpdates(currentQuestions, excludeIdentifiers);
      updates.forEach(t39 => {
        const {
          index: index_1,
          cleanedCondition
        } = t39;
        formMethods.setValue(`questions.${index_1}.condition`, cleanedCondition, {
          shouldDirty: true
        });
      });
    };
    $[95] = formMethods;
    $[96] = t38;
  } else {
    t38 = $[96];
  }
  const applyConditionUpdates = t38;
  let t39;
  if ($[97] !== applyConditionUpdates || $[98] !== confirm || $[99] !== formMethods || $[100] !== questions || $[101] !== replace || $[102] !== selectedQuestionIndex || $[103] !== t) {
    t39 = index_2 => {
      const questionToDelete = questions[index_2];
      const deletedDataIdentifier = questionToDelete && "dataIdentifier" in questionToDelete ? questionToDelete.dataIdentifier : undefined;
      confirm.open({
        title: t(_temp18),
        description: t(_temp19),
        variant: "destructive",
        primaryButton: {
          label: t(_temp20),
          onClick: () => {
            const currentQuestions_0 = formMethods.getValues("questions");
            const newQuestions = currentQuestions_0.filter((_, i) => i !== index_2);
            replace(newQuestions);
            applyConditionUpdates(deletedDataIdentifier ? new Set([deletedDataIdentifier]) : undefined);
            if (selectedQuestionIndex === index_2) {
              setSelectedQuestionIndex(null);
            } else {
              if (selectedQuestionIndex !== null && selectedQuestionIndex !== "header" && selectedQuestionIndex > index_2) {
                setSelectedQuestionIndex(selectedQuestionIndex - 1);
              }
            }
          }
        },
        secondaryButton: {
          label: t(_temp21)
        }
      });
    };
    $[97] = applyConditionUpdates;
    $[98] = confirm;
    $[99] = formMethods;
    $[100] = questions;
    $[101] = replace;
    $[102] = selectedQuestionIndex;
    $[103] = t;
    $[104] = t39;
  } else {
    t39 = $[104];
  }
  const handleDeleteQuestion = t39;
  let t40;
  if ($[105] !== applyConditionUpdates || $[106] !== move || $[107] !== questions || $[108] !== selectedQuestionIndex) {
    t40 = event => {
      const {
        active: active_0,
        over: over_0
      } = event;
      if (over_0 && active_0.id !== over_0.id) {
        const oldIndex = questions.findIndex(item => item.id === active_0.id);
        const newIndex = questions.findIndex(item_0 => item_0.id === over_0.id);
        move(oldIndex, newIndex);
        applyConditionUpdates();
        if (selectedQuestionIndex !== null && selectedQuestionIndex !== "header") {
          if (selectedQuestionIndex === oldIndex) {
            setSelectedQuestionIndex(newIndex);
          } else {
            if (oldIndex < selectedQuestionIndex && newIndex >= selectedQuestionIndex) {
              setSelectedQuestionIndex(selectedQuestionIndex - 1);
            } else {
              if (oldIndex > selectedQuestionIndex && newIndex <= selectedQuestionIndex) {
                setSelectedQuestionIndex(selectedQuestionIndex + 1);
              }
            }
          }
        }
      }
    };
    $[105] = applyConditionUpdates;
    $[106] = move;
    $[107] = questions;
    $[108] = selectedQuestionIndex;
    $[109] = t40;
  } else {
    t40 = $[109];
  }
  const handleDragEnd = t40;
  let t41;
  if ($[110] !== applyConditionUpdates || $[111] !== formMethods || $[112] !== questions || $[113] !== replace || $[114] !== selectedQuestionIndex) {
    t41 = () => {
      if (selectedQuestionIndex === null || selectedQuestionIndex === "header") {
        return;
      }
      const questionToDelete_0 = questions[selectedQuestionIndex];
      const deletedDataIdentifier_0 = questionToDelete_0 && "dataIdentifier" in questionToDelete_0 ? questionToDelete_0.dataIdentifier : undefined;
      const currentQuestions_1 = formMethods.getValues("questions");
      const newQuestions_0 = currentQuestions_1.filter((__0, i_0) => i_0 !== selectedQuestionIndex);
      replace(newQuestions_0);
      applyConditionUpdates(deletedDataIdentifier_0 ? new Set([deletedDataIdentifier_0]) : undefined);
      setSelectedQuestionIndex(null);
    };
    $[110] = applyConditionUpdates;
    $[111] = formMethods;
    $[112] = questions;
    $[113] = replace;
    $[114] = selectedQuestionIndex;
    $[115] = t41;
  } else {
    t41 = $[115];
  }
  const handleDeleteSelectedQuestion = t41;
  let t42;
  if ($[116] !== isMobile || $[117] !== selectedQuestionIndex || $[118] !== validateQuestion) {
    t42 = index_3 => {
      setSelectedButtonId(null);
      if (selectedQuestionIndex === index_3) {
        setSelectedQuestionIndex(null);
      } else {
        setSelectedQuestionIndex(index_3);
        validateQuestion(index_3);
        if (index_3 === "header") {
          setSectionOpen("basic", true);
        }
        if (isMobile) {
          requestAnimationFrame(() => settingsAreaRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start"
          }));
        }
      }
    };
    $[116] = isMobile;
    $[117] = selectedQuestionIndex;
    $[118] = validateQuestion;
    $[119] = t42;
  } else {
    t42 = $[119];
  }
  const toggleSelectedQuestionIndex = t42;
  const isPageHeaderSelected = selectedQuestionIndex === "header";
  const isQuestionSelected = typeof selectedQuestionIndex === "number";
  let t43;
  if ($[120] !== errors) {
    t43 = errors && errors.length > 0 && <div className="mb-4 flex flex-col gap-2">{errors.map(_temp22)}</div>;
    $[120] = errors;
    $[121] = t43;
  } else {
    t43 = $[121];
  }
  const t44 = !canEdit;
  const t45 = !isPageHeaderSelected;
  let t46;
  if ($[122] !== isPageHeaderSelected || $[123] !== t45) {
    t46 = cx("flex flex-col gap-2 w-[calc(100%-48px)] rounded border-2 transition-colors py-3 cursor-pointer", {
      "border-accent": isPageHeaderSelected,
      "border-transparent hover:border-tertiary": t45
    });
    $[122] = isPageHeaderSelected;
    $[123] = t45;
    $[124] = t46;
  } else {
    t46 = $[124];
  }
  let t47;
  if ($[125] !== toggleSelectedQuestionIndex) {
    t47 = () => toggleSelectedQuestionIndex("header");
    $[125] = toggleSelectedQuestionIndex;
    $[126] = t47;
  } else {
    t47 = $[126];
  }
  let t48;
  if ($[127] !== subtitle || $[128] !== tPreview || $[129] !== title) {
    t48 = title ? <><div className="text-heading-3 text-primary text-center w-full"><TemplatedText value={title} /></div>{subtitle && <div className="text-body-2 text-secondary text-center w-full"><TemplatedText value={subtitle} /></div>}</> : <><div className="text-heading-3 text-tertiary text-center w-full">{tPreview(_temp23)}</div><div className="text-body-2 text-tertiary text-center w-full">{tPreview(_temp24)}</div></>;
    $[127] = subtitle;
    $[128] = tPreview;
    $[129] = title;
    $[130] = t48;
  } else {
    t48 = $[130];
  }
  let t49;
  if ($[131] !== t44 || $[132] !== t46 || $[133] !== t47 || $[134] !== t48) {
    t49 = <button ref={pageHeaderRef} type="button" disabled={t44} className={t46} onClick={t47}>{t48}</button>;
    $[131] = t44;
    $[132] = t46;
    $[133] = t47;
    $[134] = t48;
    $[135] = t49;
  } else {
    t49 = $[135];
  }
  let t50;
  if ($[136] !== canEdit || $[137] !== handleDeleteQuestion || $[138] !== handleDragEnd || $[139] !== questions || $[140] !== selectedQuestionIndex || $[141] !== toggleSelectedQuestionIndex) {
    t50 = questions.length > 0 && <DndContext onDragEnd={handleDragEnd}><SortableContext items={questions.map(_temp25)} strategy={verticalListSortingStrategy}><div className="flex flex-col gap-1 mb-6">{questions.map((question_0, index_4) => <div key={question_0.id} className="group"><DraggableQuestion id={question_0.id} disableDrag={questions.length === 1} canEdit={canEdit}><QuestionPreview question={question_0} questionIndex={index_4} isSelected={selectedQuestionIndex === index_4} onClick={() => toggleSelectedQuestionIndex(index_4)} onDelete={() => handleDeleteQuestion(index_4)} canEdit={canEdit} /></DraggableQuestion></div>)}</div></SortableContext></DndContext>;
    $[136] = canEdit;
    $[137] = handleDeleteQuestion;
    $[138] = handleDragEnd;
    $[139] = questions;
    $[140] = selectedQuestionIndex;
    $[141] = toggleSelectedQuestionIndex;
    $[142] = t50;
  } else {
    t50 = $[142];
  }
  let t51;
  if ($[143] !== canEdit || $[144] !== handleAddContent || $[145] !== handleAddContentOpenChange) {
    t51 = canEdit && <AddContentDropdown onSelect={handleAddContent} onOpenChange={handleAddContentOpenChange} />;
    $[143] = canEdit;
    $[144] = handleAddContent;
    $[145] = handleAddContentOpenChange;
    $[146] = t51;
  } else {
    t51 = $[146];
  }
  let t52;
  if ($[147] !== t50 || $[148] !== t51) {
    t52 = <div className="px-6 py-5 w-full" ref={previewAreaRef}>{t50}{t51}</div>;
    $[147] = t50;
    $[148] = t51;
    $[149] = t52;
  } else {
    t52 = $[149];
  }
  let t53;
  if ($[150] !== t49 || $[151] !== t52) {
    t53 = <div className="flex flex-col items-center justify-center">{t49}{t52}</div>;
    $[150] = t49;
    $[151] = t52;
    $[152] = t53;
  } else {
    t53 = $[152];
  }
  let t54;
  if ($[153] !== buttons.length || $[154] !== canEdit || $[155] !== stockLabel) {
    t54 = buttons.length === 0 && !canEdit && <PreviewCta variant="primary" text={stockLabel} />;
    $[153] = buttons.length;
    $[154] = canEdit;
    $[155] = stockLabel;
    $[156] = t54;
  } else {
    t54 = $[156];
  }
  let t55;
  if ($[157] !== buttons.length || $[158] !== canEdit || $[159] !== handleSelectStockButton || $[160] !== stockLabel) {
    t55 = buttons.length === 0 && canEdit && <QuestionContainer isSelected={false} onClick={handleSelectStockButton} canEdit={true} flush={true}><PreviewCta variant="primary" text={stockLabel} /></QuestionContainer>;
    $[157] = buttons.length;
    $[158] = canEdit;
    $[159] = handleSelectStockButton;
    $[160] = stockLabel;
    $[161] = t55;
  } else {
    t55 = $[161];
  }
  let t56;
  if ($[162] !== buttons || $[163] !== canEdit || $[164] !== confirmDeleteButton || $[165] !== formState.errors || $[166] !== handleButtonDragEnd || $[167] !== selectedButtonId || $[168] !== t || $[169] !== toggleSelectedButton) {
    t56 = buttons.length > 0 && <DndContext onDragEnd={handleButtonDragEnd}><SortableContext items={buttons.map(_temp26)} strategy={verticalListSortingStrategy}>{buttons.map((button_4, index_5) => <DraggableQuestion key={button_4.id} id={button_4.id} disableDrag={buttons.length === 1} canEdit={canEdit}><QuestionContainer isSelected={selectedButtonId === button_4.id} onClick={() => toggleSelectedButton(button_4.id)} onDelete={button_4.style === "primary" ? undefined : () => confirmDeleteButton(button_4.id)} hasError={!!formState.errors.choice?.buttons?.[index_5]} canEdit={canEdit} deleteLabel={t(_temp27)} flush={true}><PreviewCta variant={button_4.style} text={button_4.label.trim() || t(_temp28, {
              index: index_5 + 1
            })} /></QuestionContainer></DraggableQuestion>)}</SortableContext></DndContext>;
    $[162] = buttons;
    $[163] = canEdit;
    $[164] = confirmDeleteButton;
    $[165] = formState.errors;
    $[166] = handleButtonDragEnd;
    $[167] = selectedButtonId;
    $[168] = t;
    $[169] = toggleSelectedButton;
    $[170] = t56;
  } else {
    t56 = $[170];
  }
  let t57;
  if ($[171] !== t54 || $[172] !== t55 || $[173] !== t56) {
    t57 = <div className="px-6 pb-6 flex flex-col gap-3">{t54}{t55}{t56}</div>;
    $[171] = t54;
    $[172] = t55;
    $[173] = t56;
    $[174] = t57;
  } else {
    t57 = $[174];
  }
  let t58;
  if ($[175] !== t53 || $[176] !== t57) {
    t58 = <PreviewPane fill={true}><BifrostFrame editable={true}>{t53}{t57}</BifrostFrame></PreviewPane>;
    $[175] = t53;
    $[176] = t57;
    $[177] = t58;
  } else {
    t58 = $[177];
  }
  let t59;
  if ($[178] !== t43 || $[179] !== t58) {
    t59 = <div className="shrink-0 flex flex-col">{t43}{t58}</div>;
    $[178] = t43;
    $[179] = t58;
    $[180] = t59;
  } else {
    t59 = $[180];
  }
  let t60;
  if ($[181] !== autocompleteItems || $[182] !== canEdit || $[183] !== handleAddButton || $[184] !== handleDeletePanelButton || $[185] !== handleDeleteSelectedQuestion || $[186] !== handleOpenStockButton || $[187] !== handleSaveValueChange || $[188] !== isPageHeaderSelected || $[189] !== isQuestionSelected || $[190] !== openButtonIds || $[191] !== openSections || $[192] !== selectedButtonId || $[193] !== selectedQuestionIndex) {
    t60 = <aside ref={settingsAreaRef} className="md:flex-1 md:min-w-0"><div className="overflow-hidden md:sticky md:top-24">{isQuestionSelected ? <QuestionSettings ref={questionSettingsRef} key={selectedQuestionIndex} questionIndex={selectedQuestionIndex} autocompleteItems={autocompleteItems} onDelete={handleDeleteSelectedQuestion} canEdit={canEdit} /> : <PageSettings autocompleteItems={autocompleteItems} canEdit={canEdit} focusTitle={isPageHeaderSelected} openSections={openSections} onSectionOpenChange={setSectionOpen} buttons={{
          selectedId: selectedButtonId,
          openIds: openButtonIds,
          onButtonOpenChange: setButtonOpen,
          onAdd: handleAddButton,
          onDelete: handleDeletePanelButton,
          onOpenStock: handleOpenStockButton,
          onSaveValueChange: handleSaveValueChange
        }} />}</div></aside>;
    $[181] = autocompleteItems;
    $[182] = canEdit;
    $[183] = handleAddButton;
    $[184] = handleDeletePanelButton;
    $[185] = handleDeleteSelectedQuestion;
    $[186] = handleOpenStockButton;
    $[187] = handleSaveValueChange;
    $[188] = isPageHeaderSelected;
    $[189] = isQuestionSelected;
    $[190] = openButtonIds;
    $[191] = openSections;
    $[192] = selectedButtonId;
    $[193] = selectedQuestionIndex;
    $[194] = t60;
  } else {
    t60 = $[194];
  }
  let t61;
  if ($[195] !== t59 || $[196] !== t60) {
    t61 = <div className="flex flex-col gap-4 w-full md:flex-row md:gap-6">{t59}{t60}</div>;
    $[195] = t59;
    $[196] = t60;
    $[197] = t61;
  } else {
    t61 = $[197];
  }
  let t62;
  if ($[198] !== handleSubmit || $[199] !== t61) {
    t62 = <form id="custom-questions-form" onSubmit={handleSubmit} aria-label="Custom questions form" className="pt-8 flex flex-col items-center md:px-2 md:pt-2 md:pb-6">{t61}</form>;
    $[198] = handleSubmit;
    $[199] = t61;
    $[200] = t62;
  } else {
    t62 = $[200];
  }
  let t63;
  if ($[201] !== formMethods || $[202] !== t62) {
    t63 = <FormProvider {...formMethods}>{t62}</FormProvider>;
    $[201] = formMethods;
    $[202] = t62;
    $[203] = t63;
  } else {
    t63 = $[203];
  }
  return t63;
};
export default PlaybookCustomQuestions;
function _temp() {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}
function _temp2($) {
  return $.defaults.option1;
}
function _temp3($_0) {
  return $_0.defaults.option2;
}
function _temp4($_1) {
  return $_1.defaults.option1;
}
function _temp5($_2) {
  return $_2.defaults.option2;
}
function _temp6($_3) {
  return $_3.defaults.option1;
}
function _temp7($_4) {
  return $_4.defaults.option2;
}
function _temp8($_5) {
  return $_5.preview.cta;
}
function _temp9(question) {
  return question.kind !== "text_block" && !question.dataIdentifier.replace(/^custom\./, "");
}
function _temp10($_6) {
  return $_6.form.dataIdentifierRequired;
}
function _temp11(t0, t1) {
  const label = t0 === undefined ? "" : t0;
  const style = t1 === undefined ? "secondary" : t1;
  return {
    id: Math.random().toString(36).substring(2) + Date.now().toString(36),
    label,
    value: "",
    style
  };
}
function _temp12($_8) {
  return $_8.buttons.confirmDelete.title;
}
function _temp13($_9) {
  return $_9.buttons.confirmDelete.descriptionWithValue;
}
function _temp14($_10) {
  return $_10.buttons.confirmDelete.description;
}
function _temp15($_11) {
  return $_11.confirmDelete.delete;
}
function _temp16($_12) {
  return $_12.confirmDelete.cancel;
}
function _temp17(current_1) {
  return current_1 === "header" ? null : current_1;
}
function _temp18($_13) {
  return $_13.confirmDelete.title;
}
function _temp19($_14) {
  return $_14.confirmDelete.description;
}
function _temp20($_15) {
  return $_15.confirmDelete.delete;
}
function _temp21($_16) {
  return $_16.confirmDelete.cancel;
}
function _temp22(error) {
  return <InlineAlert key={error.message} variant="error">{error.message}</InlineAlert>;
}
function _temp23($_17) {
  return $_17.headerPlaceholder;
}
function _temp24($_18) {
  return $_18.subheaderPlaceholder;
}
function _temp25(q) {
  return q.id;
}
function _temp26(button_3) {
  return button_3.id;
}
function _temp27($_19) {
  return $_19.preview.deleteButton;
}
function _temp28($_20) {
  return $_20.buttons.button;
}